import React, { useState, useCallback, useMemo } from 'react';
import type { Mention } from './useMentions';
import type { User } from '../../utils/userUtils';

// Type for input/textarea element refs since InputComponent was removed
type InputElementRef = React.RefObject<HTMLInputElement | HTMLTextAreaElement>;

// Unified state for both hover and tooltip functionality
interface MentionInteractionState {
  show: boolean;
  position: { x: number; y: number; showAbove?: boolean };
  user?: User;
  hyperlink?: { url: string; domain: string; text: string };
  loading?: boolean;
  type?: 'mention' | 'hyperlink';
}

// Props for hover detection functionality
interface MentionInteractionProps {
  inputRef?: InputElementRef;
  mentions?: Mention[];
  value?: string;
  enableMentions?: boolean;
  getMentionAtPosition?: (position: number) => Mention | undefined;
}

// Utility functions
const createFallbackUser = (username: string): User => ({
  id: Date.now(), // Generate temporary ID using timestamp
  username,
  email: `${username}@example.com`,
  name: username.charAt(0).toUpperCase() + username.slice(1)
});

const calculateTooltipPosition = (rect: DOMRect) => {
  const tooltipHeight = 60; // Approximate tooltip height
  const margin = 10;
  const spaceAbove = rect.top;
  const spaceBelow = window.innerHeight - rect.bottom;

  // Check if there's enough space above, otherwise use below if there's more space there
  const showAbove =
    spaceAbove >= tooltipHeight + margin || spaceAbove > spaceBelow;

  return {
    x: rect.left + rect.width / 2,
    y: showAbove ? rect.top - margin : rect.bottom + margin,
    showAbove: showAbove
  };
};

const extractDomain = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

/**
 * Unified hook for handling mention and hyperlink interactions
 * Combines hover detection, tooltip display, and click handling
 */
export function useMentionInteractions(props?: MentionInteractionProps) {
  const {
    inputRef,
    mentions = [],
    value = '',
    enableMentions = true,
    getMentionAtPosition
  } = props || {};

  const [tooltipState, setTooltipState] = useState<MentionInteractionState>({
    show: false,
    position: { x: 0, y: 0 }
  });

  // Get character position under mouse cursor (for input/textarea hover detection)
  const getCharacterPositionFromMouse = useCallback(
    (
      event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
      input: HTMLInputElement | HTMLTextAreaElement
    ): number => {
      const rect = input.getBoundingClientRect();
      const clickX = event.clientX - rect.left;

      // Account for input padding
      const style = window.getComputedStyle(input);
      const paddingLeft = parseInt(style.paddingLeft) || 0;
      const paddingRight = parseInt(style.paddingRight) || 0;

      // Adjust for padding
      const adjustedX = clickX - paddingLeft;
      const contentWidth = rect.width - paddingLeft - paddingRight;

      // Return -1 if mouse is outside text content area
      if (adjustedX < 0 || adjustedX > contentWidth) {
        return -1;
      }

      const textLength = value.length;
      if (textLength === 0) {
        return -1;
      }

      // Create a temporary span to measure text width accurately
      const tempSpan = document.createElement('span');
      tempSpan.style.font = style.font;
      tempSpan.style.fontSize = style.fontSize;
      tempSpan.style.fontFamily = style.fontFamily;
      tempSpan.style.fontWeight = style.fontWeight;
      tempSpan.style.letterSpacing = style.letterSpacing;
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'pre';
      document.body.appendChild(tempSpan);

      let charIndex = -1;

      try {
        // Binary search to find the character position
        let left = 0;
        let right = textLength;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          tempSpan.textContent = value.substring(0, mid);
          const textWidth = tempSpan.getBoundingClientRect().width;

          if (textWidth <= adjustedX) {
            charIndex = mid;
            left = mid + 1;
          } else {
            right = mid - 1;
          }
        }
      } finally {
        document.body.removeChild(tempSpan);
      }

      return charIndex;
    },
    [value]
  );

  // Handle mouse move on input/textarea to detect mention hovers
  const handleInputMouseMove = useCallback(
    (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!enableMentions || !inputRef?.current || !getMentionAtPosition)
        return;

      // Only proceed if we have mentions to check
      if (mentions.length === 0) {
        setTooltipState(prev => ({ ...prev, show: false }));
        return;
      }

      const charIndex = getCharacterPositionFromMouse(event, inputRef.current);

      // Hide tooltip if mouse is outside text area
      if (charIndex === -1) {
        setTooltipState(prev => ({ ...prev, show: false }));
        return;
      }

      // Check if the character position is within any mention
      const mention = getMentionAtPosition(charIndex);

      if (mention && charIndex >= mention.start && charIndex < mention.end) {
        const rect = inputRef.current.getBoundingClientRect();
        const position = calculateTooltipPosition(rect);
        setTooltipState({
          show: true,
          user: mention.user,
          position,
          type: 'mention',
          loading: false
        });
      } else {
        setTooltipState(prev => ({ ...prev, show: false }));
      }
    },
    [
      enableMentions,
      inputRef,
      mentions.length,
      getMentionAtPosition,
      getCharacterPositionFromMouse
    ]
  );

  // Handle mention hover (for TextWithMentions component) - no API calls
  const handleMentionHover = useCallback(
    (username: string, event: React.MouseEvent) => {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const position = calculateTooltipPosition(rect);

      // Create fallback user for display (no API calls)
      const user = createFallbackUser(username);

      setTooltipState({
        show: true,
        position,
        user,
        type: 'mention',
        loading: false
      });
    },
    []
  );

  // Handle mention hover with rich user data (for components that have full user info)
  const handleRichMentionHover = useCallback(
    (
      user: { username: string; email: string; name: string },
      event: React.MouseEvent
    ) => {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const position = calculateTooltipPosition(rect);

      setTooltipState({
        show: true,
        position,
        user: {
          id: Date.now(), // Generate temporary ID
          username: user.username,
          email: user.email,
          name: user.name
        },
        type: 'mention',
        loading: false
      });
    },
    []
  );

  // Handle hyperlink hover
  const handleHyperlinkHover = useCallback(
    (url: string, text: string, event: React.MouseEvent) => {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const position = calculateTooltipPosition(rect);
      const domain = extractDomain(url);

      setTooltipState({
        show: true,
        position,
        hyperlink: { url, domain, text },
        type: 'hyperlink',
        loading: false
      });
    },
    []
  );

  // Handle mouse leave for input
  const handleInputMouseLeave = useCallback(() => {
    setTooltipState(prev => ({ ...prev, show: false }));
  }, []);

  // Unified hide handler for better performance
  const hideTooltip = useCallback(() => {
    setTooltipState(prev =>
      prev.show
        ? { show: false, position: { x: 0, y: 0, showAbove: true } }
        : prev
    );
  }, []);

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Unified tooltip state (replaces both tooltipState from hover and tooltip from tooltip hook)
      tooltipState,
      tooltip: tooltipState, // Alias for backward compatibility

      // Input hover functionality (from useMentionHover)
      handleMouseMove: handleInputMouseMove,
      handleMouseLeave: handleInputMouseLeave,

      // Mention/hyperlink hover functionality (from useMentionTooltip)
      handleMentionHover,
      handleRichMentionHover,
      handleMentionLeave: hideTooltip,
      handleHyperlinkHover,
      handleHyperlinkLeave: hideTooltip,

      // Unified hide function
      hideTooltip
    }),
    [
      tooltipState,
      handleInputMouseMove,
      handleInputMouseLeave,
      handleMentionHover,
      hideTooltip,
      handleHyperlinkHover,
      handleRichMentionHover
    ]
  );
}
